import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Toolbar,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Link,
  Image,
  Code,
  ColorFormat,
  Undo,
  Redo
} from '@mui/icons-material';

// Sanitização rigorosa para prevenir XSS
const sanitizeHtml = (html) => {
  if (!html) return '';
  
  // Tags permitidas (apenas tags seguras)
  const allowedTags = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'a', 'span', 'div', 'blockquote', 'pre', 'code'
  ];
  
  // Atributos permitidos por tag
  const allowedAttributes = {
    'a': ['href', 'title', 'target'],
    'span': ['style'],
    'div': ['style'],
    'p': ['style'],
    'h1': ['style'],
    'h2': ['style'],
    'h3': ['style'],
    'h4': ['style'],
    'h5': ['style'],
    'h6': ['style']
  };
  
  // Propriedades CSS permitidas
  const allowedStyles = [
    'color', 'background-color', 'font-size', 'font-weight', 'font-style',
    'text-align', 'text-decoration', 'margin', 'padding'
  ];
  
  // Remove scripts e tags perigosas
  let sanitized = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
    .replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, '')
    .replace(/<input\b[^<]*\/?>/gi, '')
    .replace(/<textarea\b[^<]*(?:(?!<\/textarea>)<[^<]*)*<\/textarea>/gi, '')
    .replace(/<select\b[^<]*(?:(?!<\/select>)<[^<]*)*<\/select>/gi, '')
    .replace(/<button\b[^<]*(?:(?!<\/button>)<[^<]*)*<\/button>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/on\w+\s*=/gi, '');
  
  // Validar e limpar tags
  sanitized = sanitized.replace(/<\/?(\w+)[^>]*>/g, (match, tagName) => {
    const lowerTagName = tagName.toLowerCase();
    
    if (!allowedTags.includes(lowerTagName)) {
      return '';
    }
    
    // Se for tag de fechamento, manter
    if (match.startsWith('</')) {
      return match;
    }
    
    // Limpar atributos
    const allowedAttrs = allowedAttributes[lowerTagName] || [];
    let cleanTag = `<${lowerTagName}`;
    
    // Extrair atributos do match original
    const attrRegex = /(\w+)=["']([^"']*)["']/g;
    let attrMatch;
    
    while ((attrMatch = attrRegex.exec(match)) !== null) {
      const [, attrName, attrValue] = attrMatch;
      
      if (allowedAttrs.includes(attrName)) {
        if (attrName === 'href' && !attrValue.match(/^(https?:\/\/|mailto:|#)/)) {
          continue; // Skip links suspeitos
        }
        
        if (attrName === 'style') {
          // Limpar estilos
          const cleanStyle = cleanCssStyles(attrValue);
          if (cleanStyle) {
            cleanTag += ` ${attrName}="${cleanStyle}"`;
          }
        } else {
          // Escapar valor do atributo
          const escapedValue = attrValue
            .replace(/[<>]/g, '')
            .replace(/javascript:/gi, '')
            .replace(/vbscript:/gi, '');
          cleanTag += ` ${attrName}="${escapedValue}"`;
        }
      }
    }
    
    cleanTag += '>';
    return cleanTag;
  });
  
  return sanitized;
};

// Função para limpar estilos CSS
const cleanCssStyles = (styleString) => {
  if (!styleString) return '';
  
  const allowedStyles = [
    'color', 'background-color', 'font-size', 'font-weight', 'font-style',
    'text-align', 'text-decoration', 'margin', 'padding', 'border', 'border-radius'
  ];
  
  const styles = styleString.split(';')
    .map(style => style.trim())
    .filter(style => {
      if (!style) return false;
      const property = style.split(':')[0].trim().toLowerCase();
      return allowedStyles.includes(property);
    })
    .filter(style => !style.toLowerCase().includes('expression'))
    .filter(style => !style.toLowerCase().includes('javascript'))
    .filter(style => !style.toLowerCase().includes('vbscript'));
  
  return styles.join('; ');
};

const SecureHtmlEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Digite seu conteúdo aqui...',
  height = 400,
  readOnly = false,
  toolbar = true,
  sx = {}
}) => {
  const [content, setContent] = useState(value);
  const [linkDialog, setLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const editorRef = useRef(null);
  const [history, setHistory] = useState([value]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Sincronizar com prop value
  useEffect(() => {
    if (value !== content) {
      setContent(value);
    }
  }, [value]);

  // Salvar no histórico
  const saveToHistory = (newContent) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory.slice(-20)); // Manter apenas últimos 20
    setHistoryIndex(newHistory.length - 1);
  };

  // Undo
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const prevContent = history[newIndex];
      setContent(prevContent);
      setHistoryIndex(newIndex);
      if (onChange) onChange(prevContent);
    }
  };

  // Redo
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextContent = history[newIndex];
      setContent(nextContent);
      setHistoryIndex(newIndex);
      if (onChange) onChange(nextContent);
    }
  };

  // Executar comando
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    handleContentChange();
  };

  // Handler para mudanças no conteúdo
  const handleContentChange = () => {
    if (editorRef.current && !readOnly) {
      const newContent = editorRef.current.innerHTML;
      const sanitizedContent = sanitizeHtml(newContent);
      
      if (sanitizedContent !== content) {
        setContent(sanitizedContent);
        saveToHistory(sanitizedContent);
        if (onChange) {
          onChange(sanitizedContent);
        }
      }
    }
  };

  // Handler para colar conteúdo
  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text/plain');
    const sanitizedPaste = sanitizeHtml(paste);
    document.execCommand('insertHTML', false, sanitizedPaste);
    handleContentChange();
  };

  // Inserir link
  const handleInsertLink = () => {
    if (linkUrl && linkText) {
      const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      document.execCommand('insertHTML', false, linkHtml);
      handleContentChange();
    }
    setLinkDialog(false);
    setLinkUrl('');
    setLinkText('');
  };

  // Toolbar personalizada
  const ToolbarComponent = () => (
    <Paper elevation={1} sx={{ p: 1, mb: 1, border: '1px solid #e0e0e0' }}>
      <ButtonGroup variant="outlined" size="small" sx={{ mr: 1 }}>
        <Tooltip title="Desfazer">
          <IconButton 
            onClick={handleUndo} 
            disabled={historyIndex <= 0}
            size="small"
          >
            <Undo />
          </IconButton>
        </Tooltip>
        <Tooltip title="Refazer">
          <IconButton 
            onClick={handleRedo} 
            disabled={historyIndex >= history.length - 1}
            size="small"
          >
            <Redo />
          </IconButton>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup variant="outlined" size="small" sx={{ mr: 1 }}>
        <Tooltip title="Negrito">
          <IconButton onClick={() => execCommand('bold')} size="small">
            <FormatBold />
          </IconButton>
        </Tooltip>
        <Tooltip title="Itálico">
          <IconButton onClick={() => execCommand('italic')} size="small">
            <FormatItalic />
          </IconButton>
        </Tooltip>
        <Tooltip title="Sublinhado">
          <IconButton onClick={() => execCommand('underline')} size="small">
            <FormatUnderlined />
          </IconButton>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup variant="outlined" size="small" sx={{ mr: 1 }}>
        <Tooltip title="Lista com marcadores">
          <IconButton onClick={() => execCommand('insertUnorderedList')} size="small">
            <FormatListBulleted />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lista numerada">
          <IconButton onClick={() => execCommand('insertOrderedList')} size="small">
            <FormatListNumbered />
          </IconButton>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup variant="outlined" size="small" sx={{ mr: 1 }}>
        <Tooltip title="Alinhar à esquerda">
          <IconButton onClick={() => execCommand('justifyLeft')} size="small">
            <FormatAlignLeft />
          </IconButton>
        </Tooltip>
        <Tooltip title="Centralizar">
          <IconButton onClick={() => execCommand('justifyCenter')} size="small">
            <FormatAlignCenter />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinhar à direita">
          <IconButton onClick={() => execCommand('justifyRight')} size="small">
            <FormatAlignRight />
          </IconButton>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup variant="outlined" size="small" sx={{ mr: 1 }}>
        <Tooltip title="Inserir link">
          <IconButton onClick={() => setLinkDialog(true)} size="small">
            <Link />
          </IconButton>
        </Tooltip>
      </ButtonGroup>

      <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 1 }}>
        <Select
          defaultValue="p"
          onChange={(e) => execCommand('formatBlock', e.target.value)}
        >
          <MenuItem value="p">Parágrafo</MenuItem>
          <MenuItem value="h1">Título 1</MenuItem>
          <MenuItem value="h2">Título 2</MenuItem>
          <MenuItem value="h3">Título 3</MenuItem>
          <MenuItem value="h4">Título 4</MenuItem>
          <MenuItem value="blockquote">Citação</MenuItem>
        </Select>
      </FormControl>
    </Paper>
  );

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {toolbar && !readOnly && <ToolbarComponent />}
      
      <Paper 
        variant="outlined" 
        sx={{ 
          minHeight: height, 
          p: 2,
          '& .editor-content': {
            minHeight: height - 32,
            outline: 'none',
            '&:focus': {
              outline: 'none'
            }
          }
        }}
      >
        <div
          ref={editorRef}
          className="editor-content"
          contentEditable={!readOnly}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
          onInput={handleContentChange}
          onPaste={handlePaste}
          style={{
            minHeight: height - 32,
            outline: 'none',
            lineHeight: 1.6,
            fontFamily: 'inherit',
            fontSize: '14px'
          }}
          placeholder={placeholder}
          suppressContentEditableWarning={true}
        />
        
        {!content && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              position: 'absolute',
              top: 16,
              left: 16,
              pointerEvents: 'none',
              opacity: 0.6
            }}
          >
            {placeholder}
          </Typography>
        )}
      </Paper>

      {/* Dialog para inserir links */}
      <Dialog open={linkDialog} onClose={() => setLinkDialog(false)}>
        <DialogTitle>Inserir Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Texto do link"
            fullWidth
            variant="outlined"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://exemplo.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleInsertLink} 
            variant="contained"
            disabled={!linkUrl || !linkText}
          >
            Inserir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecureHtmlEditor;